import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { ModalEnum } from 'src/app/core/enums/modal.enum';
import { LocationAddress } from 'src/app/core/interfaces/locationAddress';
import { ThirdParty, ThirdPartyAddress } from 'src/app/core/interfaces/thirdParty';
import { FormMessages } from 'src/app/core/messages/form-messages.enum';
import { ServiceMessages } from 'src/app/core/messages/service-messages.enum';
import { Utils } from 'src/app/core/resources/utils';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { ModalSelectionMapComponent } from 'src/app/shared/modal-selection-map/modal-selection-map.component';
import { ThirdPartyConsignmentService } from '../../cargo/manual-creation-cargo/components/third-party-consignment/third-party-consignment.service';
import { Permission } from 'src/app/core/resources/permission';
import { PermissionRole } from 'src/app/core/resources/permission-role';
import { FormThirdPartyConsignmentComponent } from '../../cargo/manual-creation-cargo/components/form-third-party-consignment/form-third-party-consignment.component';
import { CompanyManager } from 'src/app/core/managers/company.manager';

@Component({
  selector: 'app-third-party-address-item',
  templateUrl: './third-party-address-item.component.html',
  styleUrls: ['./third-party-address-item.component.scss']
})
export class ThirdPartyAddressItemComponent {
  @Input() thirdPartyAddress: ThirdPartyAddress;
  @Input() city: string;
  @Input() addressId: string;
  @Input() thirdParty: ThirdParty;
  @Output() refreshList = new EventEmitter<void>();
  permission = Permission;

  constructor(
    public dialog: MatDialog,
    public utils: Utils,
    private spinner: NgxSpinnerService,
    private snackBarService: SnackBarService,
    private thirdPartyConsignmentService: ThirdPartyConsignmentService,
    private permissionRole: PermissionRole,
    private companyManager: CompanyManager
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['thirdPartyAddress'] && this.thirdPartyAddress && this.addressId) {
      if (this.thirdPartyAddress.addressId === this.addressId) this.editThirdPartyAddressDialog();
    }
  }

  get showLocationError(): boolean {
    const id = this.utils.getNestedValue(this.thirdPartyAddress, 'address.id');
    const latitude = this.utils.getNestedValue(this.thirdPartyAddress, 'address.location.lat');
    const longitude = this.utils.getNestedValue(this.thirdPartyAddress, 'address.location.lng');
    return !!id && (!latitude || !longitude);
  }

  get showIDError(): { show: boolean, reason?: string } {
    const { disable, reason } = this.utils.disableThirdPartyAddress(this.thirdPartyAddress);
    return { show: disable, reason };
  }

  get canAddLatLng(): boolean {
    return this.permissionRole.hasPermission(this.permission.administration.module, this.permission.administration.createThirdPartyAddress);
  }

  get isActiveThirdPartyAddress(): boolean {
    return this.utils.thirdPartyAddressIsActive(this.thirdPartyAddress);
  }


  get isActiveCompany(): boolean {
    return true;
  }

  get hasPermissionToEnableDisableThirdParty(): boolean {
    return this.permissionRole.hasPermission(
      this.permission.administration.module,
      this.permission.administration.disableEnableThirdParty
    );
  }

  openAddLatLng() {
    const dialogConfig = new MatDialogConfig();
    const municipalityCode = this.utils.getNestedValue(this.thirdPartyAddress, 'municipalityCode');
    dialogConfig.data = {
      clickMap: true,
      // Habilita polígono + validación inline en la modal si conocemos el municipio.
      ...(municipalityCode ? { municipalityCode } : {})
    };
    dialogConfig.height = ModalEnum.FORMS_HEIGHT;
    dialogConfig.maxHeight = ModalEnum.MAX_HEIGHT;
    dialogConfig.width = ModalEnum.MEDIUM_WIDTH;
    dialogConfig.maxWidth = ModalEnum.MAX_WIDTH;
    dialogConfig.autoFocus = false;
    this.dialog.open(ModalSelectionMapComponent, dialogConfig).afterClosed().subscribe(result => {
      if (result && result.state && result.data && result.data.location && result.data.location.lat && result.data.location.lng)
        this.setLatLngAddress(result.data.location);
    });
  }

  checkLocation() {
    const dialogConfig = new MatDialogConfig();
    const thirdPartyToSend = this.getThirdPartyAddressToSend();
    if (!thirdPartyToSend) return;
    dialogConfig.data = {
      initialValue: { address: thirdPartyToSend.address, location: thirdPartyToSend.location },
      municipalityCode: this.utils.getNestedValue(this.thirdPartyAddress, 'municipalityCode')
    };
    dialogConfig.height = ModalEnum.MEDIUM_HEIGHT;
    dialogConfig.maxHeight = ModalEnum.MAX_HEIGHT;
    dialogConfig.width = ModalEnum.EXTRA_SMALL_WIDTH;
    dialogConfig.maxWidth = ModalEnum.MAX_WIDTH;
    dialogConfig.autoFocus = false;
    this.dialog.open(ModalSelectionMapComponent, dialogConfig);
  }

  private getThirdPartyAddressToSend() {
    const inputAddress = this.utils.getNestedValue(this.thirdPartyAddress, 'address.address');
    const inputLocation = this.utils.getNestedValue(this.thirdPartyAddress, 'address.location');
    if (!inputAddress || !inputLocation || !inputLocation.lat || !inputLocation.lng) {
      this.snackBarService.openSnackBar('Esta dirección no tiene una ubicación geográfica asignada.', undefined, 'alert');
      return null;
    }
    return {
      address: inputAddress,
      location: inputLocation
    };
  }


  private setLatLngAddress(location: LocationAddress) {
    const thirdPartyToSend: ThirdPartyAddress = this.utils.clone(this.thirdPartyAddress);
    thirdPartyToSend.address['location'] = {
      lat: Number(location.lat),
      lng: Number(location.lng)
    }
    this.thirdPartyConsignmentService.editThirdPartyAddress(thirdPartyToSend, async () => {
      const { found, thirdPartyAddress } = await this.getThirdPartyAddress(thirdPartyToSend.address.address, this.thirdPartyAddress.information.document, this.thirdPartyAddress.municipalityCode);
      const addressLat = this.utils.getNestedValue(thirdPartyAddress, 'address.location.lat');
      const addressLng = this.utils.getNestedValue(thirdPartyAddress, 'address.location.lng');
      if (found && addressLat && addressLng) {
        this.snackBarService.openSnackBar(FormMessages.ADDRESS_LOCATION_SETTED);
        this.thirdPartyAddress = thirdPartyAddress;
      } else this.snackBarService.openSnackBar(ServiceMessages.ADDRESS_LOCATION_NOT_SETTED, undefined, 'error');
    });
  }

  editThirdPartyAddressDialog() {
    this.thirdPartyConsignmentService.editThirdPartyAddressDialog(this.thirdParty, this.thirdPartyAddress, null, () => {
      this.refreshList.emit();
      this.snackBarService.openSnackBar(FormMessages.ADDRESS_EDITED);
    });
  }

  private async getThirdPartyAddress(address: string, companyId: string, municipalityCode: string): Promise<{ found: boolean, thirdPartyAddress: ThirdPartyAddress }> {
    let listThirdPartiesAddresses: ThirdPartyAddress[] = [];
    try {
      listThirdPartiesAddresses = await this.thirdPartyConsignmentService.getThirdPartiesAddressByCompany(companyId, null, null, municipalityCode).toPromise();
    } catch (error) {
      console.error(error);
    }
    if (!listThirdPartiesAddresses.length) return { found: false, thirdPartyAddress: null };
    const thirdPartyAddress = listThirdPartiesAddresses.find((thirdParty: ThirdPartyAddress) => {
      const hasSameCompany = thirdParty && thirdParty.information && companyId && thirdParty.information.document === companyId;
      const hasSameAddress = !address || (address && thirdParty && thirdParty.address && thirdParty.address.address === address);
      const hasSameMunicipality = !municipalityCode || (municipalityCode && thirdParty.municipalityCode === municipalityCode);
      return hasSameCompany && hasSameAddress && hasSameMunicipality;
    });
    return { found: !!thirdPartyAddress, thirdPartyAddress };
  }

  async setThirdPartyAddressState(state: boolean): Promise<void> {
    let description = 'Active';
    if (!state) {
      const response = await this.companyManager.openDialogConfirmWithDescription(`Escriba la razón por la cual desea desactivar esta dirección de tercero`, "Motivo");
      if (!response.confirm) return;
      description = response.description;
    } else {
      const response = await this.companyManager.openDialogConfirm(`¿Estás seguro que deseas activar esta dirección de tercero?`);
      if (!response.confirm) return;
    }
    this.spinner.show();
    let success = false;
    try {
      const addressId = this.thirdPartyAddress.addressId;
      if (!addressId) {
        this.snackBarService.openSnackBar('No se pudo obtener el ID de la dirección de tercero', undefined, 'error');
        return;
      }
      const response = await this.thirdPartyConsignmentService.updateThirdPartyAddressState(addressId, state, description).toPromise();
      success = !!(response && response.message);
    } catch (error) {
      if (error && error.error && error.error.message) {
        this.snackBarService.openSnackBar(error.error.message, undefined, 'error');
      } else {
        this.snackBarService.openSnackBar('Ocurrió un error al actualizar el estado de la dirección de tercero', undefined, 'error');
      }
    } finally {
      this.spinner.hide();
    }
    if (success) {
      this.snackBarService.openSnackBar('Estado de la dirección de tercero actualizado correctamente');
      this.refreshList.emit();
    }
  }

}
